<template>
    <DashboardLayout ref="dashboardLayout">
        <Breadcrumb class="flex breadcrumb" :model="breadcrumbItems" />

        <div class="card flex flex-column justify-content-center pt-4 px-4">

            <div style="color: #222; font-weight: 500; margin: 16px 0 8px 0;">
                <span style="font-size: 1.2em; margin-right: 6px;">⚠️</span>
                <b>Warning:</b> Class requests are automatically rejected if not accepted before the class starts.
            </div>

            <div v-if="viewStack.length > 1" class="mb-4">
                <Button label="Back" icon="pi pi-arrow-left" class="p-button-text back-button cursor-pointer"
                    @click="goBack" />
            </div>

            <div v-if="currentView.type === 'list' && studentsData.length">
                <RequestList :students="studentsData" @view-request="pickClass" />
            </div>

            <div v-else-if="currentView.type === 'Request'">
                <RequestView :request="currentView.data" @view-class="scheduleClass" @class-rejected="classRejected"
                    @open-chat="handleOpenChat" />
            </div>

            <div v-else-if="currentView.type === 'RescheduleRequest'">
                <RescheduleRequestDetails :request="currentView.data" @back="goBack" @request-handled="classRejected"
                    @reschedule-class="rescheduleClass" @open-chat="handleOpenChat" />
            </div>

            <div v-else-if="currentView.type === 'RequestClass'">
                <RequestClass :request="currentView.data" :conflicts="scheduleConflicts"
                    :unregistered-students="unregisteredStudents" @submit-request="submitSchedule" />
            </div>

            <div v-else-if="currentView.type === 'RescheduleClass'">
                <RescheduleClass :request="currentView.data" :conflicts="rescheduleConflicts" @submit-request="submitReschedule" />
            </div>

            <div v-else class="flex align-items-center justify-content-center h-full">
                <div class="flex flex-column">
                    <span class="Caption text-gray-700 text-center">You don’t have any class requests yet</span>
                </div>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import Breadcrumb from 'primevue/breadcrumb';
import Button from "primevue/button";

import DashboardLayout from "@/layout/DashboardLayout.vue";
import RequestList from '@/components/dashboard/tutor/classRequests/requestList.vue'
import RequestView from '@/components/dashboard/tutor/classRequests/requestView.vue'
import RequestClass from '@/components/dashboard/tutor/classRequests/scheduleClass.vue'
import RescheduleClass from '@/components/dashboard/tutor/classRequests/rescheduleClass.vue'
import RescheduleRequestDetails from '@/components/dashboard/tutor/classRequests/rescheduleRequestDetails.vue'

import * as api from '@/api/tutor';
import { notifyContrast, notifyError, notifyWarn } from "@/utils/notification";
import { unregisteredEmailsFromError } from "@/utils/otherStudents";
import { useRouter } from "vue-router";

// --- COMPOSITION FUNCTIONS ---

/**
 * Manages the stack-based navigation for nested views.
 */
const useViewNavigation = (initialLabel) => {
    const viewStack = ref([
        { type: "list", label: initialLabel, data: null }
    ]);

    const currentView = computed(() =>
        viewStack.value[viewStack.value.length - 1]
    );

    const pushView = (type, data) => {
        let label = '';
        if (type === "Request") {
            label = "Class Request";
        } else if (type === "RequestClass") {
            label = "Request New Class";
        } else if (type === "RescheduleRequest") {
            label = "Reschedule Request";
        } else if (type === "RescheduleClass") {
            label = "Reschedule Class";
        } else {
            label = type;
        }
        viewStack.value.push({ type, label, data });
    };

    const goBack = () => {
        if (viewStack.value.length > 1) {
            viewStack.value.pop();
        }
    };

    // Breadcrumb items derived from the stack
    const breadcrumbItems = computed(() => {
        return [
            { label: "Dashboard" },
            ...viewStack.value.map(v => ({ label: v.label }))
        ];
    });

    return { viewStack, currentView, pushView, goBack, breadcrumbItems };
};


// --- STATE & DATA ---

const studentsData = ref([]);
const scheduleConflicts = ref({ message: '', items: [] });
const rescheduleConflicts = ref({ message: '', items: [] });
const unregisteredStudents = ref([]);
const router = useRouter();
const toast = useToast();

// --- VIEW NAVIGATION ---
const { viewStack, currentView, pushView, goBack, breadcrumbItems } = useViewNavigation("Class Requests");


// --- METHODS ---
const fetchClassRequests = async () => {
    try {
        const response = await api.fetchRequests();
        studentsData.value = response.data.data;
    } catch (error) {
        notifyError("Error", "Failed to fetch class requests.");
    }
};

const pickClass = (data) => {
    if (data.isRescheduleRequest) {
        pushView("RescheduleRequest", data);
    } else {
        pushView("Request", data);
    }
}

const scheduleClass = (data) => {
    pushView("RequestClass", data);
}

const rescheduleClass = (data) => {
    pushView("RescheduleClass", data);
}

const classRejected = () => {
    goBack();
    fetchClassRequests();
}

const submitSchedule = async (data) => {
    scheduleConflicts.value = { message: '', items: [] };
    unregisteredStudents.value = [];
    try {
        notifyContrast("Please wait...", "We are processing your request.");
        const classId = currentView.value.data._id;
        await api.scheduleClass(classId, data);
        toast.removeAllGroups();
        notifyContrast("Waiting for approval!", "We've notified the student. They'll review the schedule and give you a confirmation shortly.");
        router.push('/tutor_dashboard');
    } catch (error) {
        toast.removeAllGroups();
        const unregisteredEmails = unregisteredEmailsFromError(error);
        if (error.response?.data?.code === 409) {
            scheduleConflicts.value = {
                message: error.response.data.message || '',
                items: error.response.data.data || []
            };
        } else if (error.response?.data?.code === 403) {
            notifyWarn("Plan Limit Reached", error.response.data.message);
        } else if (unregisteredEmails.length > 0) {
            unregisteredStudents.value = unregisteredEmails;
            notifyError("Unregistered students", error.response.data.message);
        } else {
            notifyError(error.response?.data?.status || "Error", error.response?.data?.message || "Failed to schedule class.");
        }
    }
}

const submitReschedule = async (data) => {
    rescheduleConflicts.value = { message: '', items: [] };
    try {
        notifyContrast("Please wait...", "We are processing your request.");
        const requestId = currentView.value.data._id;
        const payload = {
            status: 'accepted',
            ...data
        };
        await api.acceptRescheduleRequest(requestId, payload);
        toast.removeAllGroups();
        notifyContrast("Success", "Class rescheduled successfully.");
        router.push('/tutor_dashboard');
    } catch (error) {
        toast.removeAllGroups();
        if (error.response?.data?.status === 409 || error.response?.status === 409) {
            rescheduleConflicts.value = {
                message: error.response.data.message || '',
                items: error.response.data.data || []
            };
        } else {
            notifyError("Error", error.response?.data?.message || "Failed to reschedule.");
        }
    }
}

// --- LIFECYCLE HOOKS ---
onMounted(async () => {
    await fetchClassRequests();
});

const dashboardLayout = ref(null);

const handleOpenChat = (channel) => {
    if (dashboardLayout.value) {
        if (channel && typeof dashboardLayout.value.openSelectedChat === 'function') {
            dashboardLayout.value.openSelectedChat(channel);
        } else if (typeof dashboardLayout.value.openChatList === 'function') {
            dashboardLayout.value.openChatList();
        }
    }
}
</script>

<style scoped>
.back-button {
    color: var(--gray-600);
}

.back-button:hover {
    color: var(--gray-600) !important;
    background-color: var(--gray-200) !important;
}

.back-button:active {
    color: var(--gray-600) !important;
}

.breadcrumb {
    display: flex !important;
    flex-wrap: wrap !important;
    overflow-x: hidden !important;
}

.text-gray-700 {
    color: var(--gray-700) !important;
    padding-top: 30vh;
}

@media (max-width: 767px) {
    .breadcrumb {
        font-size: 12px;
    }
}
</style>